import { useEffect, useMemo, useState } from 'react'
import { Lightbulb, CalendarClock, Loader2, CheckCheck, AlertCircle, ChevronDown, ChevronUp, X, Plus } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { apiRequest } from '@/lib/apiClient'
import { createTask, updateTaskStatus, deleteTask } from '@/lib/apiClient'

// ─── Types ────────────────────────────────────────────────────────────────────

type TaskStatus = 'idea' | 'planned' | 'in_progress' | 'done'
type TaskCategory = 'searchline' | 'openclaw' | 'product-idea' | 'infrastructure'

interface TaskItem {
  id: string
  title: string
  notes: string | null
  status: TaskStatus
  category: TaskCategory
  assignee: string | null
  position: number
  created_at: string
  updated_at: string
}

// ─── Column config ────────────────────────────────────────────────────────────

const COLUMNS: { status: TaskStatus; label: string; icon: React.ReactNode }[] = [
  { status: 'idea',        label: 'Ideas',       icon: <Lightbulb className="h-4 w-4" /> },
  { status: 'planned',     label: 'Planned',     icon: <CalendarClock className="h-4 w-4" /> },
  { status: 'in_progress', label: 'In Progress', icon: <Loader2 className="h-4 w-4" /> },
  { status: 'done',        label: 'Done',        icon: <CheckCheck className="h-4 w-4" /> },
]

const STATUS_LABELS: Record<TaskStatus, string> = {
  idea: 'Ideas',
  planned: 'Planned',
  in_progress: 'In Progress',
  done: 'Done',
}

// ─── Category config ──────────────────────────────────────────────────────────

const CATEGORY_STYLES: Record<TaskCategory, string> = {
  'searchline':    'bg-blue-500/15 text-blue-400 border-blue-500/30',
  'openclaw':      'bg-purple-500/15 text-purple-400 border-purple-500/30',
  'product-idea':  'bg-orange-500/15 text-orange-400 border-orange-500/30',
  'infrastructure':'bg-green-500/15 text-green-400 border-green-500/30',
}

const CATEGORY_LABELS: Record<TaskCategory, string> = {
  'searchline':    'Searchline',
  'openclaw':      'OpenClaw',
  'product-idea':  'Product Ideas',
  'infrastructure':'Infrastructure',
}

const CATEGORY_EMOJI: Record<TaskCategory, string> = {
  'searchline':    '🔵',
  'openclaw':      '🟣',
  'product-idea':  '🟠',
  'infrastructure':'🟢',
}

// Text colour for tile headings (extracted from badge styles)
const CATEGORY_TEXT: Record<TaskCategory, string> = {
  'searchline':    'text-blue-400',
  'openclaw':      'text-purple-400',
  'product-idea':  'text-orange-400',
  'infrastructure':'text-green-400',
}

const ALL_CATEGORIES: TaskCategory[] = ['searchline', 'openclaw', 'product-idea', 'infrastructure']

// ─── Task card ────────────────────────────────────────────────────────────────

const TaskCard = ({
  task,
  onDelete,
  onStatusChange,
}: {
  task: TaskItem
  onDelete: (id: string) => void
  onStatusChange: (id: string, status: TaskStatus) => void
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [showMoveMenu, setShowMoveMenu] = useState(false)
  const categoryStyle = CATEGORY_STYLES[task.category] ?? 'bg-gray-500/15 text-gray-400'
  const categoryLabel = CATEGORY_LABELS[task.category] ?? task.category

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (window.confirm('Delete this task?')) {
      onDelete(task.id)
    }
  }

  const handleMoveClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowMoveMenu((prev) => !prev)
  }

  const handleMoveTo = (e: React.MouseEvent, status: TaskStatus) => {
    e.stopPropagation()
    setShowMoveMenu(false)
    onStatusChange(task.id, status)
  }

  const otherStatuses = (Object.keys(STATUS_LABELS) as TaskStatus[]).filter(
    (s) => s !== task.status
  )

  return (
    <Card
      className="p-3 space-y-2 bg-card border border-border hover:border-border/80 transition-colors cursor-pointer select-none relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setShowMoveMenu(false) }}
      onClick={() => setIsExpanded((prev) => !prev)}
    >
      {/* Delete button — visible on hover */}
      {isHovered && (
        <button
          onClick={handleDelete}
          className="absolute top-2 right-2 text-muted-foreground hover:text-red-400 transition-colors z-10 p-0.5 rounded"
          title="Delete task"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium leading-snug text-foreground pr-4">{task.title}</p>
      </div>

      {task.notes && (
        <div className="transition-all duration-200">
          <p
            className={`text-xs text-muted-foreground leading-relaxed cursor-pointer ${
              isExpanded ? '' : 'line-clamp-3'
            }`}
          >
            {task.notes}
          </p>
          <div className="flex justify-center pt-1">
            {isExpanded ? (
              <ChevronUp className="h-3 w-3 text-muted-foreground/50" />
            ) : (
              <ChevronDown className="h-3 w-3 text-muted-foreground/50" />
            )}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap pt-1">
        <Badge className={`text-[10px] px-2 py-0.5 border rounded-full font-medium ${categoryStyle}`}>
          {categoryLabel}
        </Badge>
        {task.assignee && (
          <span className="text-[10px] text-muted-foreground font-mono">@{task.assignee}</span>
        )}

        {/* Move to dropdown */}
        <div className="relative ml-auto" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={handleMoveClick}
            className="text-[10px] text-muted-foreground hover:text-foreground border border-border/50 hover:border-border rounded px-1.5 py-0.5 transition-colors flex items-center gap-1"
          >
            Move <ChevronDown className="h-2.5 w-2.5" />
          </button>
          {showMoveMenu && (
            <div className="absolute right-0 bottom-full mb-1 bg-popover border border-border rounded-md shadow-lg z-20 min-w-[110px] py-1">
              {otherStatuses.map((s) => (
                <button
                  key={s}
                  onClick={(e) => handleMoveTo(e, s)}
                  className="w-full text-left text-xs px-3 py-1.5 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

// ─── Inline add form ──────────────────────────────────────────────────────────

const InlineAddTask = ({
  status,
  onAdd,
}: {
  status: TaskStatus
  onAdd: (title: string, status: TaskStatus) => Promise<void>
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return
    setSaving(true)
    try {
      await onAdd(title.trim(), status)
      setTitle('')
      setIsOpen(false)
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors w-full py-1 px-1 rounded hover:bg-muted/50"
      >
        <Plus className="h-3.5 w-3.5" />
        Add task
      </button>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2 p-2 bg-card border border-border rounded-lg">
      <input
        autoFocus
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Task title…"
        className="text-sm bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground"
      />
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={saving || !title.trim()}
          className="text-xs bg-[#FD802E] hover:bg-[#FD802E]/90 disabled:opacity-50 text-white px-3 py-1 rounded transition-colors"
        >
          {saving ? 'Adding…' : 'Add'}
        </button>
        <button
          type="button"
          onClick={() => { setIsOpen(false); setTitle('') }}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

// ─── Category summary tile ────────────────────────────────────────────────────

const CategoryTile = ({
  category,
  tasks,
}: {
  category: TaskCategory
  tasks: TaskItem[]
}) => {
  const total = tasks.length
  const ideas      = tasks.filter((t) => t.status === 'idea').length
  const planned    = tasks.filter((t) => t.status === 'planned').length
  const inProgress = tasks.filter((t) => t.status === 'in_progress').length
  const done       = tasks.filter((t) => t.status === 'done').length

  const label   = CATEGORY_LABELS[category]
  const emoji   = CATEGORY_EMOJI[category]
  const textCol = CATEGORY_TEXT[category]

  return (
    <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg px-4 py-3 flex flex-col gap-1 min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="text-sm">{emoji}</span>
        <span className={`text-sm font-semibold ${textCol}`}>{label}</span>
      </div>
      <div className="text-2xl font-bold text-foreground leading-none">{total}</div>
      <div className="text-[11px] text-muted-foreground leading-relaxed">
        {ideas} ideas · {planned} planned · {inProgress} in progress · {done} done
      </div>
    </div>
  )
}

// ─── Column ───────────────────────────────────────────────────────────────────

const KanbanColumn = ({
  status,
  label,
  icon,
  tasks,
  onDelete,
  onStatusChange,
  onAddTask,
}: {
  status: TaskStatus
  label: string
  icon: React.ReactNode
  tasks: TaskItem[]
  onDelete: (id: string) => void
  onStatusChange: (id: string, status: TaskStatus) => void
  onAddTask: (title: string, status: TaskStatus) => Promise<void>
}) => {
  const columnHeaderStyle: Record<TaskStatus, string> = {
    idea:        'text-orange-400',
    planned:     'text-blue-400',
    in_progress: 'text-yellow-400',
    done:        'text-green-400',
  }

  const isEmpty = tasks.length === 0

  return (
    <div className="flex flex-col gap-3 min-w-0">
      {/* Column header */}
      <div className={`flex items-center gap-2 pb-2 border-b border-border ${columnHeaderStyle[status]}`}>
        {icon}
        <span className="text-sm font-semibold">{label}</span>
        <span className="ml-auto text-xs text-muted-foreground bg-muted rounded-full px-2 py-0.5">
          {tasks.length}
        </span>
      </div>

      {/* Inline add */}
      <InlineAddTask status={status} onAdd={onAddTask} />

      {/* Cards */}
      <div className="flex flex-col gap-2">
        {isEmpty ? (
          <p className="text-xs text-muted-foreground italic text-center py-4">
            {status === 'in_progress'
              ? 'Nothing in progress — pick something from Planned'
              : 'Nothing here yet'}
          </p>
        ) : (
          tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onDelete={onDelete}
              onStatusChange={onStatusChange}
            />
          ))
        )}
      </div>
    </div>
  )
}

// ─── TaskBoard page ───────────────────────────────────────────────────────────

export const TaskBoard = () => {
  const [tasks, setTasks] = useState<TaskItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updatedAt] = useState<string>(() => {
    const now = new Date()
    return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  })

  const loadTasks = () => {
    setLoading(true)
    setError(null)
    apiRequest<TaskItem[]>({ path: '/api/engine/tasks' })
      .then((data) => setTasks(data))
      .catch((err) => setError(err?.message ?? 'Failed to load tasks'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadTasks()
  }, [])

  const handleDelete = async (id: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== id))
    try {
      await deleteTask(id)
    } catch {
      // Restore on failure
      loadTasks()
    }
  }

  const handleStatusChange = async (id: string, status: TaskStatus) => {
    setTasks((prev) => prev.map((t) => t.id === id ? { ...t, status } : t))
    try {
      await updateTaskStatus(id, status)
    } catch {
      loadTasks()
    }
  }

  const handleAddTask = async (title: string, status: TaskStatus) => {
    const newTask = await createTask({ title, status, assignee: 'Sax' }) as unknown as TaskItem
    setTasks((prev) => [...prev, newTask])
  }

  const tasksByCategory = useMemo(
    () =>
      Object.fromEntries(
        ALL_CATEGORIES.map((cat) => [cat, tasks.filter((t) => t.category === cat)])
      ) as Record<TaskCategory, TaskItem[]>,
    [tasks]
  )

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin text-[#FD802E]" />
        <span className="text-sm">Loading tasks…</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-6 w-6 text-red-400" />
        <p className="text-sm text-red-400 font-medium">Unable to load tasks. Try refreshing.</p>
        <button
          onClick={loadTasks}
          className="text-xs text-gray-300 hover:text-white border border-white/10 px-4 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="px-6 py-8 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Task Board</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Company-wide — Searchline · OpenClaw · Product Ideas · Infrastructure
          </p>
        </div>
        <span className="text-xs text-muted-foreground mt-1 shrink-0">Updated: {updatedAt}</span>
      </div>

      {/* Category summary tiles */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-8">
        {ALL_CATEGORIES.map((cat) => (
          <CategoryTile key={cat} category={cat} tasks={tasksByCategory[cat]} />
        ))}
      </div>

      {/* Kanban columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        {COLUMNS.map(({ status, label, icon }) => (
          <KanbanColumn
            key={status}
            status={status}
            label={label}
            icon={icon}
            tasks={tasks.filter((t) => t.status === status)}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onAddTask={handleAddTask}
          />
        ))}
      </div>
    </div>
  )
}
